import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import Loading from "components/Loading";
import ZipSlotAdsTabs from "components/dashboard/zipSlotAds/ZipSlotAdsTabs";
import ZipCodeAddField from "components/dashboard/zipSlotAds/ZipCodeAddField";
import {
  REPORT_ENTRY_SCAN_LIMIT,
} from "components/dashboard/zipSlotAds/zipSlotAdsConstants";
import {
  aggregateClickMetrics,
  callableErrorMessage,
  formatCents,
  formatLedgerTimestamp,
  resolveInstitutionId,
  sanitizeRankSlots,
  uniqueRelatedZips,
} from "components/dashboard/zipSlotAds/zipSlotAdsHelpers";
import { useAuth } from "hooks/useAuth";
import {
  getOwnZipSlotBid,
  getZipSlotRanksForDisplay,
  listLedgerPage,
} from "services/zipSlotAds/zipSlotAdsService";

const AdPerformancePage = () => {
  const { userData, tokenClaims, organizationId, userLoading } = useAuth();
  const institutionId = resolveInstitutionId({
    userData,
    tokenClaims,
    organizationId,
  });

  const [entries, setEntries] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [hitScanLimit, setHitScanLimit] = useState(false);
  const [rankRows, setRankRows] = useState([]);
  const [rankError, setRankError] = useState("");
  const [lookupZips, setLookupZips] = useState([]);

  const load = useCallback(async () => {
    if (!institutionId) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    setLoadError("");
    try {
      const collected = [];
      let startAfterDoc = null;
      let hasMore = true;
      while (hasMore && collected.length < REPORT_ENTRY_SCAN_LIMIT) {
        const remaining = REPORT_ENTRY_SCAN_LIMIT - collected.length;
        const page = await listLedgerPage({
          institutionId,
          pageSize: Math.min(100, remaining),
          startAfterDoc,
        });
        collected.push(...page.rows);
        startAfterDoc = page.lastDoc;
        hasMore = page.hasMore;
      }
      setHitScanLimit(hasMore && collected.length >= REPORT_ENTRY_SCAN_LIMIT);
      setEntries(collected);
    } catch (err) {
      setLoadError(
        callableErrorMessage(err, "Unable to load performance transactions."),
      );
    } finally {
      setLoaded(true);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = useMemo(() => aggregateClickMetrics(entries), [entries]);
  const zipsToShow = useMemo(() => {
    const fromLedger = uniqueRelatedZips(entries);
    return Array.from(new Set([...fromLedger, ...lookupZips]));
  }, [entries, lookupZips]);

  useEffect(() => {
    if (!institutionId || zipsToShow.length === 0) {
      setRankRows([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      zipsToShow.map(async (zip) => {
        const [rankDoc, ownBid] = await Promise.all([
          getZipSlotRanksForDisplay(zip),
          getOwnZipSlotBid(zip, institutionId),
        ]);
        const ownSlot = sanitizeRankSlots(
          rankDoc?.slots,
          institutionId,
        ).find((slot) => slot.isOwn);
        const clickRow = metrics.byZip.find((row) => row.zip === zip);
        return {
          zip,
          currentRank: ownSlot?.rank || null,
          currentBidCents: ownBid?.bidPerClickCents ?? null,
          bidStatus: ownBid?.status || null,
          clickCount: clickRow?.clickCount || 0,
          clickSpendCents: clickRow?.clickSpendCents || 0,
          ranksUpdatedAt: rankDoc?.updatedAt || null,
        };
      }),
    )
      .then((rows) => {
        if (!cancelled) {
          setRankRows(rows);
          setRankError("");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setRankError(
            callableErrorMessage(err, "Unable to load current slot ranks."),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId, zipsToShow, metrics.byZip]);

  const handleAddZip = (zip) => {
    setLookupZips((prev) => (prev.includes(zip) ? prev : [...prev, zip]));
  };

  if (userLoading || (institutionId && !loaded)) {
    return <Loading page />;
  }

  if (!institutionId) {
    return (
      <Container maxWidth="lg">
        <DashboardPageHeader
          title="Ad performance"
          subtitle="Clicks, spend, and current slot rank."
        />
        <ZipSlotAdsTabs />
        <Alert severity="warning">
          No institution is linked to your account.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <DashboardPageHeader
        title="Ad performance"
        subtitle="Click count, click spend, and current slot rank per ZIP. Impression counts are not available yet."
      />
      <ZipSlotAdsTabs />

      <Alert severity="info" sx={{ mb: 2 }}>
        Impression counts are not in this report. Prompt 1 did not log
        impressions; those events come from the carousel client in Prompt 3.
        This page only reports clicks, spend, and current rank.
      </Alert>
      <Alert severity="info" sx={{ mb: 3 }}>
        Slot-position history over time is not stored on zip-slot bid documents
        (each bid overwrites the current amount and status). Only the current
        rank from zipSlotRanks is shown, plus the rank document&apos;s last
        update time.
      </Alert>

      {loadError ? (
        <DataLoadErrorAlert message={loadError} onRetry={load} />
      ) : null}
      {rankError ? <DataLoadErrorAlert message={rankError} /> : null}
      {hitScanLimit ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing the most recent {REPORT_ENTRY_SCAN_LIMIT} ledger entries.
          Older clicks are not included in these totals.
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Clicks
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {metrics.clickCount}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Click spend
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {formatCents(metrics.clickSpendCents)}
          </Typography>
        </Paper>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Add a ZIP you bid on that has no clicks yet to see its current rank.
      </Typography>
      <ZipCodeAddField
        onAdd={handleAddZip}
        helperText="Look up a ZIP you bid on that has no clicks yet"
      />

      {rankRows.length === 0 ? (
        <Typography color="text.secondary">
          No click activity or ZIP lookups yet.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ZIP</TableCell>
                <TableCell>Current rank</TableCell>
                <TableCell>Your bid</TableCell>
                <TableCell>Clicks</TableCell>
                <TableCell>Click spend</TableCell>
                <TableCell>Rank last updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankRows.map((row) => (
                <TableRow key={row.zip}>
                  <TableCell>{row.zip}</TableCell>
                  <TableCell>
                    {row.currentRank ? `#${row.currentRank}` : "Not in top 7"}
                  </TableCell>
                  <TableCell>
                    {row.currentBidCents != null
                      ? formatCents(row.currentBidCents)
                      : "—"}
                  </TableCell>
                  <TableCell>{row.clickCount}</TableCell>
                  <TableCell>{formatCents(row.clickSpendCents)}</TableCell>
                  <TableCell>
                    {formatLedgerTimestamp(row.ranksUpdatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdPerformancePage;
